import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export type TopFolderZipJob = {
  jobId: string;
  label: string;
  progress: number;
  hideProgressBanner?: boolean;
};

export type TopFolderZipReady = {
  jobId: string;
  label: string;
};

export type TopFolderZipState = {
  jobs: Record<string, TopFolderZipJob>;
  ready: Record<string, TopFolderZipReady>;
};

const initialState: TopFolderZipState = {
  jobs: {},
  ready: {},
};

const topFolderZipSlice = createSlice({
  name: "topFolderZip",
  initialState,
  reducers: {
    startTopFolderZipJob: (
      state,
      action: PayloadAction<{ itemKey: string; label: string }>,
    ) => {
      state.jobs[action.payload.itemKey] = {
        jobId: "",
        label: action.payload.label,
        progress: 0,
      };
    },
    setTopFolderZipJobId: (
      state,
      action: PayloadAction<{ itemKey: string; jobId: string }>,
    ) => {
      const j = state.jobs[action.payload.itemKey];
      if (!j) return;
      j.jobId = action.payload.jobId;
    },
    setTopFolderZipProgress: (
      state,
      action: PayloadAction<{ itemKey: string; progress: number }>,
    ) => {
      const j = state.jobs[action.payload.itemKey];
      if (!j) return;
      j.progress = action.payload.progress;
    },
    suppressTopFolderZipProgressBanner: (
      state,
      action: PayloadAction<{ itemKey: string }>,
    ) => {
      const j = state.jobs[action.payload.itemKey];
      if (!j) return;
      j.hideProgressBanner = true;
    },
    removeTopFolderZipJob: (
      state,
      action: PayloadAction<{ itemKey: string }>,
    ) => {
      delete state.jobs[action.payload.itemKey];
    },
    setTopFolderZipReady: (
      state,
      action: PayloadAction<{
        itemKey: string;
        jobId: string;
        label: string;
      }>,
    ) => {
      const { itemKey, jobId, label } = action.payload;
      state.ready[itemKey] = { jobId, label };
    },
    clearTopFolderZipReady: (
      state,
      action: PayloadAction<{ itemKey: string }>,
    ) => {
      delete state.ready[action.payload.itemKey];
    },
    hydrateTopFolderZip: (
      state,
      action: PayloadAction<Pick<TopFolderZipState, "jobs" | "ready">>,
    ) => {
      state.jobs = action.payload.jobs ?? {};
      state.ready = action.payload.ready ?? {};
    },
    resetTopFolderZip: () => initialState,
  },
});

export const {
  startTopFolderZipJob,
  setTopFolderZipJobId,
  setTopFolderZipProgress,
  suppressTopFolderZipProgressBanner,
  removeTopFolderZipJob,
  setTopFolderZipReady,
  clearTopFolderZipReady,
  hydrateTopFolderZip,
  resetTopFolderZip,
} = topFolderZipSlice.actions;

export default topFolderZipSlice.reducer;
